import { Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { RoleBadge } from '@/containers/AdminUsers/RoleBadge'
import API from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { User } from '@/types/User'

import { ConfirmButton } from '../ConfirmButton'
import { Button } from '../ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'

export const UsersTable = ({
  users,
  refresh,
}: {
  users: User[]
  refresh: () => void
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'settings.users',
  })
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)

  // const { data: session } = useSession()
  const { user } = useAuth({
    middleware: 'auth',
    redirectTo: '/auth/login',
    tmp: 'UsersTable',
  })

  const onDeleteUser = (userId: string) => {
    API.delete(`/api/v2/users/${userId}`, {
      headers: {
        'Content-Type': 'multipart/form-data',
        // Authorization: `Bearer ${session!.token}`,
      },
    })
      .then(() => {
        toast.success(t('delete.success'))
        setSelectedUserId(null)
        refresh()
      })
      .catch(() => {
        toast.error(t('delete.error'))
      })
  }

  return (
    <div className="overflow-hidden rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>Uživatel</TableHead>
            <TableHead>{t('list.roles')}</TableHead>
            <TableHead className="w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id} className="group">
              <TableCell className="max-w-0">
                <div className="flex items-center gap-2">
                  <p className="truncate font-medium">{user.fullName || user.email}</p>
                  {!user.verified && <RoleBadge muted>{t('list.state.unverified')}</RoleBadge>}
                </div>
                {user.fullName && <p className="truncate text-xs text-muted-foreground">{user.email}</p>}
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {user.roles.map((role) => (
                    <RoleBadge key={role}>{t(`roles.${role}`)}</RoleBadge>
                  ))}
                </div>
              </TableCell>
              <TableCell className="text-right">
                {!user.roles.includes('owner') && (
                  <ConfirmButton
                    open={selectedUserId === user.id}
                    onOpenChange={(open: boolean) => {
                      if (!open) {
                        setSelectedUserId(null)
                      }
                    }}
                    title={t('delete.action') + ' ' + user.fullName}
                    description={t('delete.description')}
                    onConfirm={() => {
                      onDeleteUser(user.id)
                    }}
                  >
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t('delete.action')}
                      className="transition-opacity hover:text-danger md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100"
                      onClick={() => setSelectedUserId(user.id)}
                    >
                      <Trash2 />
                    </Button>
                  </ConfirmButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
